import React, { useEffect, useState } from 'react';
import { userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useConfirm } from '../components/ConfirmModal';
import { FiMail, FiPhone, FiPlus, FiX, FiEdit2, FiTrash2, FiBriefcase, FiLogOut, FiRefreshCw } from 'react-icons/fi';

const ROLE_COLORS = { admin:{bg:'#fee2e2',txt:'#991b1b'}, manager:{bg:'#ede9fe',txt:'#5b21b6'}, team:{bg:'#dbeafe',txt:'#1e40af'}, client:{bg:'#d1fae5',txt:'#065f46'} };
const CLIENT_TYPE_LABELS = { direct_india_no_gst:'Direct Indian (No GST)', direct_india_gst:'Direct Indian (With GST)', upwork_personal:'Upwork Personal', upwork_company:'Upwork Company', wise:'Wise', paypal:'PayPal', referral:'Referral', other:'Other' };
const CLIENT_TYPE_ICONS  = { direct_india_no_gst:'🇮🇳', direct_india_gst:'🧾', upwork_personal:'💼', upwork_company:'🏢', wise:'💳', paypal:'🅿️', referral:'🤝', other:'🔖' };

const EMPTY_OFFBOARD = { exitDate: new Date().toISOString().slice(0,10), exitReason: '', reassignToId: '' };

export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const confirm = useConfirm();

  const [members, setMembers]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modal, setModal]             = useState(false);
  const [editModal, setEditModal]     = useState(null);
  const [offboardModal, setOffboardModal] = useState(null); // { member }
  const [offboardForm, setOffboardForm]   = useState(EMPTY_OFFBOARD);
  const [showFormer, setShowFormer]   = useState(false);

  const [form, setForm] = useState({
    name:'', email:'', password:'', role:'team', phone:'', company:'',
    position:'', clientType:'', department:'', dateOfBirth:'', joiningDate:''
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      // Admin needs former members too for the offboarding section
      const r = await userAPI.getAll(isAdmin ? { includeFormer: '1' } : {});
      setMembers(r.data.users || []);
    } catch { toast.error('Failed to load team'); }
    setLoading(false);
  };

  const createMember = async () => {
    if (!form.name || !form.email || !form.password) return toast.error('Name, email and password required');
    if (form.role === 'client' && !form.clientType) return toast.error('Please select a client type');
    try {
      await userAPI.create(form);
      toast.success('Member added!');
      setModal(false);
      setForm({ name:'', email:'', password:'', role:'team', phone:'', company:'', position:'', clientType:'', department:'', dateOfBirth:'', joiningDate:'' });
      load();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const updateMember = async () => {
    try {
      const { _newPassword, ...data } = editModal;
      if (_newPassword && _newPassword.trim()) data.password = _newPassword.trim();
      await userAPI.update(editModal._id, data);
      toast.success('Updated'); setEditModal(null); load();
    } catch { toast.error('Failed'); }
  };

  const deleteMember = async (id) => {
    const ok = await confirm('This will permanently remove the member and all their records.', { title: 'Delete Member?', variant: 'danger' });
    if (!ok) return;
    try { await userAPI.delete(id); toast.success('Member deleted'); load(); }
    catch(e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  };

  const openOffboard = (m) => {
    setOffboardForm({ ...EMPTY_OFFBOARD });
    setOffboardModal(m);
  };

  const submitOffboard = async () => {
    if (!offboardForm.exitDate) return toast.error('Exit date is required');
    try {
      await userAPI.offboard(offboardModal._id, offboardForm);
      toast.success(`${offboardModal.name} has been offboarded`);
      setOffboardModal(null);
      load();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const reactivateMember = async (m) => {
    const ok = await confirm(`Reactivate ${m.name}? They will be able to log in again.`, { title: 'Reactivate Member?' });
    if (!ok) return;
    try {
      await userAPI.reactivate(m._id);
      toast.success(`${m.name} reactivated`);
      load();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ width:40, height:40, border:'3px solid #e2e8f0', borderTop:'3px solid #6366f1', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
    </div>
  );

  const S = {
    page: { maxWidth:1280, margin:'0 auto', fontFamily:"'Sora',sans-serif" },
    btn: (v, sm) => ({
      padding: sm ? '7px 14px' : '10px 20px', borderRadius:10, border:'none',
      fontWeight:600, fontSize: sm ? 12 : 14, cursor:'pointer',
      display:'inline-flex', alignItems:'center', gap:6,
      ...(v==='primary' ? { background:'var(--pm-gradient)', color:'#fff' }
        : v==='danger'  ? { background:'#fee2e2', color:'#ef4444', border:'none' }
        : v==='amber'   ? { background:'#fef3c7', color:'#92400e', border:'none' }
        : { background:'#f8fafc', color:'#64748b', border:'1.5px solid #e2e8f0' })
    }),
    inp: { width:'100%', padding:'9px 13px', border:'2px solid #e2e8f0', borderRadius:9, fontSize:14, outline:'none', boxSizing:'border-box', marginTop:4 },
    overlay: { position:'fixed', inset:0, background:'#00000060', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 },
    modal: { background:'#fff', borderRadius:20, padding:32, width:'100%', maxWidth:520, maxHeight:'90vh', overflowY:'auto' },
    label: { display:'block', fontSize:13, fontWeight:600, color:'#475569', marginBottom:12 },
  };

  const activeMembers  = members.filter(m => m.isActive !== false);
  const formerMembers  = members.filter(m => m.isActive === false);
  const activePeers    = activeMembers.filter(m => (m.id || m._id) !== (user?.id || user?._id));

  const teamByRole = {
    admin:   activeMembers.filter(m => m.role === 'admin'),
    manager: activeMembers.filter(m => m.role === 'manager'),
    team:    activeMembers.filter(m => m.role === 'team'),
  };

  const canOffboard = (m) =>
    isAdmin &&
    (m.id || m._id) !== (user?.id || user?._id) &&
    (m.role === 'team' || m.role === 'manager');

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap');
        @keyframes spin { to { transform:rotate(360deg) } }
      `}</style>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28 }}>
        <div>
          <h1 style={{ margin:0, fontSize:28, fontWeight:800, color:'#0f172a' }}>Team</h1>
          <p style={{ margin:'4px 0 0', color:'#64748b' }}>
            {activeMembers.length} active
            {formerMembers.length > 0 && ` · ${formerMembers.length} former`}
          </p>
        </div>
        {isAdmin && <button style={S.btn('primary')} onClick={() => setModal(true)}><FiPlus/> Add Member</button>}
      </div>

      {/* Active members by role */}
      {Object.entries(teamByRole).map(([role, list]) => {
        if (!list.length) return null;
        const rc = ROLE_COLORS[role] || ROLE_COLORS.team;
        const isAdminSection = role === 'admin';
        return (
          <div key={role} style={{ marginBottom:32 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
              <span style={{ background:rc.bg, color:rc.txt, padding:'4px 14px', borderRadius:20, fontSize:12, fontWeight:700, textTransform:'uppercase' }}>{role}</span>
              <span style={{ fontSize:13, color:'#94a3b8', fontWeight:600 }}>{list.length}</span>
            </div>

            {isAdminSection ? (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {list.map(m => (
                  <div key={m._id} style={{ background:'linear-gradient(135deg,#f8f5ff 0%,#fff 100%)', borderRadius:16, padding:'20px 28px', boxShadow:'0 2px 12px #0000000a', border:'1.5px solid #ede9fe', position:'relative', display:'flex', alignItems:'center', gap:24 }}>
                    {isAdmin && (m.id||m._id) !== (user.id||user._id) && (user.role==='admin' || m.role==='team' || m.role==='client') && (
                      <div style={{ position:'absolute', top:16, right:16, display:'flex', gap:6 }}>
                        <button style={S.btn('ghost',true)} onClick={() => setEditModal({...m})}><FiEdit2 size={12}/></button>
                        <button style={S.btn('danger',true)} onClick={() => deleteMember(m._id)}><FiTrash2 size={12}/></button>
                      </div>
                    )}
                    <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:26, flexShrink:0 }}>{m.name?.charAt(0).toUpperCase()}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:800, fontSize:18, color:'#0f172a' }}>{m.name}</div>
                      {m.company && <div style={{ fontSize:13, color:'#94a3b8', marginTop:2 }}>{m.company}</div>}
                      {m.clientType && <div style={{ fontSize:11, color:'#059669', fontWeight:600, marginTop:2 }}>{CLIENT_TYPE_ICONS[m.clientType]} {CLIENT_TYPE_LABELS[m.clientType]}</div>}
                    </div>
                    <div style={{ display:'flex', gap:28, flexWrap:'wrap' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#475569' }}><FiMail size={13}/>{m.email}</div>
                      {m.phone && <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#475569' }}><FiPhone size={13}/>{m.phone}</div>}
                      {m.position && <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--pm-primary)', fontWeight:700 }}><FiBriefcase size={12}/>{m.position}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
                {list.map(m => (
                  <div key={m._id} style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px #0000000a', border:'1px solid #f1f5f9', position:'relative' }}>
                    {isAdmin && (m.id||m._id) !== (user.id||user._id) && (user.role==='admin' || m.role==='team' || m.role==='client') && (
                      <div style={{ position:'absolute', top:16, right:16, display:'flex', gap:6 }}>
                        <button style={S.btn('ghost',true)} onClick={() => setEditModal({...m})}><FiEdit2 size={12}/></button>
                        {canOffboard(m) && (
                          <button style={S.btn('amber',true)} title="Offboard member" onClick={() => openOffboard(m)}><FiLogOut size={12}/></button>
                        )}
                        <button style={S.btn('danger',true)} onClick={() => deleteMember(m._id)}><FiTrash2 size={12}/></button>
                      </div>
                    )}
                    <div style={{ display:'flex', gap:14, alignItems:'center', marginBottom:14 }}>
                      <div style={{ width:52, height:52, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:22, flexShrink:0, overflow:'hidden', padding:0 }}>
                        {m.avatar ? <img src={m.avatar} alt={m.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : m.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight:700, fontSize:16, color:'#0f172a' }}>{m.name}</div>
                        {m.company && <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>{m.company}</div>}
                        {m.clientType && <div style={{ fontSize:11, color:'#059669', fontWeight:600, marginTop:2 }}>{CLIENT_TYPE_ICONS[m.clientType]} {CLIENT_TYPE_LABELS[m.clientType]}</div>}
                      </div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#475569' }}><FiMail size={13}/>{m.email}</div>
                      {m.phone && <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#475569' }}><FiPhone size={13}/>{m.phone}</div>}
                      {m.position && <div style={{ fontSize:12, color:'var(--pm-primary)', fontWeight:600 }}><FiBriefcase size={11}/> {m.position}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Former Members Section */}
      {formerMembers.length > 0 && (
        <div style={{ marginTop:32 }}>
          <div
            style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, cursor:'pointer', userSelect:'none' }}
            onClick={() => setShowFormer(v => !v)}
          >
            <span style={{ background:'#f1f5f9', color:'#64748b', padding:'4px 14px', borderRadius:20, fontSize:12, fontWeight:700, textTransform:'uppercase' }}>Former Members</span>
            <span style={{ fontSize:13, color:'#94a3b8', fontWeight:600 }}>{formerMembers.length}</span>
            <span style={{ fontSize:11, color:'#94a3b8' }}>{showFormer ? '▲ hide' : '▼ show'}</span>
          </div>

          {showFormer && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
              {formerMembers.map(m => (
                <div key={m._id} style={{ background:'#f8fafc', borderRadius:16, padding:20, boxShadow:'none', border:'1.5px dashed #cbd5e1', position:'relative', opacity:0.85 }}>
                  <div style={{ display:'flex', gap:14, alignItems:'center', marginBottom:12 }}>
                    <div style={{ width:48, height:48, borderRadius:'50%', background:'#cbd5e1', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:20, flexShrink:0, overflow:'hidden', padding:0 }}>
                      {m.avatar ? <img src={m.avatar} alt={m.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : m.name?.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:15, color:'#475569' }}>{m.name}</div>
                      <div style={{ fontSize:12, color:'#94a3b8' }}>{m.position || m.role}</div>
                    </div>
                    {isAdmin && (
                      <button style={S.btn('ghost', true)} title="Reactivate member" onClick={() => reactivateMember(m)}>
                        <FiRefreshCw size={12}/> Reactivate
                      </button>
                    )}
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#64748b' }}><FiMail size={12}/>{m.email}</div>
                    {m.exitDate && (
                      <div style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:11, fontWeight:700, color:'#dc2626', background:'#fee2e2', borderRadius:8, padding:'3px 10px', width:'fit-content', marginTop:4 }}>
                        🚪 Left {m.exitDate}
                      </div>
                    )}
                    {m.exitReason && (
                      <div style={{ fontSize:12, color:'#94a3b8', marginTop:4, fontStyle:'italic' }}>"{m.exitReason}"</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Member Modal */}
      {modal && (
        <div style={S.overlay} onClick={e => e.target===e.currentTarget && setModal(false)}>
          <div style={S.modal}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
              <h3 style={{ margin:0, fontSize:20, fontWeight:700 }}>Add Member</h3>
              <button style={{ background:'none', border:'none', cursor:'pointer', fontSize:24 }} onClick={() => setModal(false)}><FiX/></button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <label style={{ ...S.label, gridColumn:'1/-1' }}>Full Name *<input style={S.inp} value={form.name} onChange={e => setForm({...form, name:e.target.value})}/></label>
              <label style={{ ...S.label, gridColumn:'1/-1' }}>Email *<input type="email" style={S.inp} value={form.email} onChange={e => setForm({...form, email:e.target.value})}/></label>
              <label style={{ ...S.label, gridColumn:'1/-1' }}>Password *<input type="password" style={S.inp} value={form.password} onChange={e => setForm({...form, password:e.target.value})}/></label>
              <label style={S.label}>Role
                <select style={S.inp} value={form.role} onChange={e => setForm({...form, role:e.target.value, clientType:''})}>
                  <option value="team">Team</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  <option value="client">Client</option>
                </select>
              </label>
              <label style={S.label}>Phone<input style={S.inp} value={form.phone} onChange={e => setForm({...form, phone:e.target.value})}/></label>
              <label style={S.label}>Company / Organisation<input style={S.inp} value={form.company} onChange={e => setForm({...form, company:e.target.value})}/></label>
              {form.role === 'client' ? (
                <label style={S.label}>Client Type *
                  <select style={S.inp} value={form.clientType} onChange={e => setForm({...form, clientType:e.target.value})}>
                    <option value="">— Select type —</option>
                    {Object.entries(CLIENT_TYPE_LABELS).map(([v,l]) => <option key={v} value={v}>{CLIENT_TYPE_ICONS[v]} {l}</option>)}
                  </select>
                </label>
              ) : (<>
                <label style={S.label}>Position / Job Title<input style={S.inp} value={form.position} onChange={e => setForm({...form, position:e.target.value})} placeholder="e.g. Frontend Developer"/></label>
                <label style={S.label}>Department<input style={S.inp} value={form.department} onChange={e => setForm({...form, department:e.target.value})} placeholder="e.g. Engineering"/></label>
                <label style={S.label}>Date of Birth<input type="date" style={S.inp} value={form.dateOfBirth} onChange={e => setForm({...form, dateOfBirth:e.target.value})}/></label>
                <label style={S.label}>Joining Date<input type="date" style={S.inp} value={form.joiningDate} onChange={e => setForm({...form, joiningDate:e.target.value})}/></label>
              </>)}
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:12 }}>
              <button style={S.btn('ghost')} onClick={() => setModal(false)}>Cancel</button>
              <button style={S.btn('primary')} onClick={createMember}><FiPlus/> Add Member</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {editModal && (
        <div style={S.overlay} onClick={e => e.target===e.currentTarget && setEditModal(null)}>
          <div style={S.modal}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
              <h3 style={{ margin:0, fontSize:20, fontWeight:700 }}>Edit Member</h3>
              <button style={{ background:'none', border:'none', cursor:'pointer', fontSize:24 }} onClick={() => setEditModal(null)}><FiX/></button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[['Full Name','name','text'],['Phone','phone','text'],['Company / Organisation','company','text']].map(([l,k,t]) => (
                <label key={k} style={{ ...S.label, gridColumn: k==='name' ? '1/-1' : undefined }}>{l}
                  <input style={S.inp} type={t} value={editModal[k]||''} onChange={e => setEditModal({...editModal,[k]:e.target.value})}/>
                </label>
              ))}
              {editModal.role === 'client' ? (<>
                <label style={S.label}>Client Type
                  <select style={S.inp} value={editModal.clientType||''} onChange={e => setEditModal({...editModal,clientType:e.target.value})}>
                    <option value="">— Select type —</option>
                    {Object.entries(CLIENT_TYPE_LABELS).map(([v,l]) => <option key={v} value={v}>{CLIENT_TYPE_ICONS[v]} {l}</option>)}
                  </select>
                </label>
                <label style={S.label}>Country<input style={S.inp} value={editModal.country||''} onChange={e => setEditModal({...editModal,country:e.target.value})} placeholder="India / USA / UK"/></label>
                <label style={{ ...S.label, gridColumn:'1/-1' }}>Address<input style={S.inp} value={editModal.address||''} onChange={e => setEditModal({...editModal,address:e.target.value})} placeholder="123 Main St, City"/></label>
                <label style={S.label}>GST Number<input style={S.inp} value={editModal.gstNumber||''} onChange={e => setEditModal({...editModal,gstNumber:e.target.value})} placeholder="22AAAAA0000A1Z5"/></label>
              </>) : (<>
                <label style={S.label}>Position / Job Title<input style={S.inp} value={editModal.position||''} onChange={e => setEditModal({...editModal,position:e.target.value})} placeholder="e.g. Frontend Designer"/></label>
                <label style={S.label}>Department<input style={S.inp} value={editModal.department||''} onChange={e => setEditModal({...editModal,department:e.target.value})} placeholder="e.g. Engineering"/></label>
                <label style={S.label}>Date of Birth<input type="date" style={S.inp} value={editModal.dateOfBirth||''} onChange={e => setEditModal({...editModal,dateOfBirth:e.target.value})}/></label>
                <label style={S.label}>Joining Date<input type="date" style={S.inp} value={editModal.joiningDate||''} onChange={e => setEditModal({...editModal,joiningDate:e.target.value})}/></label>
              </>)}
              <label style={S.label}>Role
                <select style={S.inp} value={editModal.role} onChange={e => setEditModal({...editModal,role:e.target.value})}>
                  <option value="team">Team</option><option value="manager">Manager</option><option value="admin">Admin</option><option value="client">Client</option>
                </select>
              </label>
              <label style={{ ...S.label, gridColumn:'1/-1' }}>Reset Password <span style={{ color:'#94a3b8', fontWeight:400, textTransform:'none' }}>(leave blank to keep current)</span>
                <input style={S.inp} type="password" placeholder="New password..." value={editModal._newPassword||''} onChange={e => setEditModal({...editModal,_newPassword:e.target.value})}/>
              </label>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:12 }}>
              <button style={S.btn('ghost')} onClick={() => setEditModal(null)}>Cancel</button>
              <button style={S.btn('primary')} onClick={updateMember}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Offboard Modal */}
      {offboardModal && (
        <div style={S.overlay} onClick={e => e.target===e.currentTarget && setOffboardModal(null)}>
          <div style={{ ...S.modal, maxWidth:460 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ margin:0, fontSize:20, fontWeight:700, color:'#b45309' }}>🚪 Offboard Member</h3>
              <button style={{ background:'none', border:'none', cursor:'pointer', fontSize:24 }} onClick={() => setOffboardModal(null)}><FiX/></button>
            </div>

            {/* Member preview */}
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'#fff7ed', borderRadius:12, marginBottom:20, border:'1.5px solid #fed7aa' }}>
              <div style={{ width:44, height:44, borderRadius:'50%', background:'#f97316', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:18, flexShrink:0 }}>{offboardModal.name?.charAt(0).toUpperCase()}</div>
              <div>
                <div style={{ fontWeight:700, fontSize:15, color:'#0f172a' }}>{offboardModal.name}</div>
                <div style={{ fontSize:12, color:'#92400e' }}>{offboardModal.position || offboardModal.role} · {offboardModal.email}</div>
              </div>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <label style={S.label}>Exit Date *
                <input type="date" style={S.inp} value={offboardForm.exitDate} onChange={e => setOffboardForm({...offboardForm, exitDate:e.target.value})}/>
              </label>
              <label style={S.label}>Reason for Leaving <span style={{ color:'#94a3b8', fontWeight:400 }}>(optional)</span>
                <input style={S.inp} value={offboardForm.exitReason} onChange={e => setOffboardForm({...offboardForm, exitReason:e.target.value})} placeholder="Resignation, contract end, etc."/>
              </label>
              <label style={S.label}>Reassign Tasks & Projects To <span style={{ color:'#94a3b8', fontWeight:400 }}>(optional)</span>
                <select style={S.inp} value={offboardForm.reassignToId} onChange={e => setOffboardForm({...offboardForm, reassignToId:e.target.value})}>
                  <option value="">— Don't reassign —</option>
                  {activePeers
                    .filter(p => p.role !== 'client' && (p.id||p._id) !== (offboardModal.id||offboardModal._id))
                    .map(p => <option key={p._id} value={p._id}>{p.name} ({p.role})</option>)
                  }
                </select>
              </label>
            </div>

            <div style={{ background:'#fff7ed', borderRadius:10, padding:'12px 16px', marginTop:16, fontSize:13, color:'#92400e', border:'1px solid #fed7aa' }}>
              ⚠️ The member's projects and tasks will <strong>remain intact</strong>. Their account will be deactivated. If you choose a reassignment target, all their assignments will also be given to that person.
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
              <button style={S.btn('ghost')} onClick={() => setOffboardModal(null)}>Cancel</button>
              <button style={{ ...S.btn('primary'), background:'linear-gradient(135deg,#f97316,#ef4444)' }} onClick={submitOffboard}>
                <FiLogOut size={14}/> Confirm Offboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
